/**
 * The roster, built from primitives.
 *
 * No rigs, no glTF, no loaders — v1 has none anywhere and adding an asset
 * pipeline for five characters would be the single largest new dependency in the
 * client. Five distinct SILHOUETTES is what actually matters at gameplay
 * distance: at 16 units on a phone you read shape and colour, not topology.
 *
 * Every model:
 *   - stands 2 units tall, centred on origin (feet at y = -1), matching the
 *     sim's capsule: PLAYER_RADIUS 0.5, PLAYER_HALF_HEIGHT 1.0
 *   - is tinted by CHARACTERS[id].colorHex and nothing else. The colour is a
 *     pure function of the character, which is the whole point of the fix to
 *     v1's `setHSL(Math.random(), …)`
 *   - is a THREE.Group whose children are Meshes one level deep, so the hull
 *     outline can mirror it without walking a tree
 */

import * as THREE from 'three/webgpu';
import { CHARACTERS } from '@chaos/protocol';
import { createPlayerToonMaterial, createAccentMaterial } from '../ToonMaterials.js';
import { addHullOutline } from '../CheapOutline.js';
import { outlineMode, onOutlineModeChange } from '../PostProcessing.js';
import { blueprintFor } from './proc/blueprint.js';
import { assembleCharacter } from './proc/assemble.js';
import { createPoseDriver } from './proc/pose.js';

/** Geometry is shared across every instance of a character. Materials are not. */
const _geoCache = new Map();

function geo(key, build) {
  let g = _geoCache.get(key);
  if (!g) {
    g = build();
    _geoCache.set(key, g);
  }
  return g;
}

function mesh(geometry, material, x, y, z) {
  const m = new THREE.Mesh(geometry, material);
  m.position.set(x, y, z);
  m.castShadow = true;
  return m;
}

/** The full v1 face: eye + pupil + highlight dot + mouth. The highlight is what
 *  makes the eyes read as ALIVE instead of painted on (v1 PlayerVisuals.js:21-46).
 *  `y`/`z` are per-character: a fixed height buried Monolith's and Talon's eyes
 *  inside their torso geometry and floated Hex's at the neck. Eyes must sit ON
 *  the head surface — z is tuned so the sphere protrudes, never tangent. */
function addFace(group, { y = 0.42, z, scale = 1, gap = 0.17, mouthDrop = 0.2 } = {}) {
  const eyeGeo = geo('eye', () => new THREE.SphereGeometry(0.13, 10, 10));
  const pupilGeo = geo('pupil', () => new THREE.SphereGeometry(0.07, 8, 8));
  const highlightGeo = geo('eyeHighlight', () => new THREE.SphereGeometry(0.025, 6, 6));
  const mouthGeo = geo('mouth', () => new THREE.BoxGeometry(0.15, 0.03, 0.02));
  const eyeMat = createAccentMaterial(0xffffff);
  const pupilMat = createAccentMaterial(0x0b0b18);

  for (const side of [-gap, gap]) {
    const eye = mesh(eyeGeo, eyeMat, side * scale, y, z);
    eye.scale.setScalar(scale);
    eye.userData.noOutline = true;
    const pupil = new THREE.Mesh(pupilGeo, pupilMat);
    pupil.position.set(0, 0, 0.08);
    pupil.userData.noOutline = true;
    eye.add(pupil);
    // Inherits the eye's scale through the hierarchy, so it sizes per silhouette.
    const highlight = new THREE.Mesh(highlightGeo, eyeMat);
    highlight.position.set(0.03, 0.03, 0.06);
    highlight.userData.noOutline = true;
    pupil.add(highlight);
    group.add(eye);
  }

  const mouth = mesh(mouthGeo, pupilMat, 0, y - mouthDrop * scale, z + 0.05 * scale);
  mouth.scale.setScalar(scale);
  mouth.castShadow = false;
  mouth.userData.noOutline = true;
  group.add(mouth);
}

/** Ground contact ring. Sells "this thing is standing here" on a flat floor. */
function addGlowRing(group, color) {
  const ring = new THREE.Mesh(
    geo('ring', () => new THREE.TorusGeometry(0.62, 0.07, 8, 24)),
    createAccentMaterial(color, { transparent: true, opacity: 0.32 })
  );
  ring.rotation.x = Math.PI / 2;
  ring.position.y = -0.98;
  ring.userData.noOutline = true;
  group.add(ring);
}

// ─── The five silhouettes ───────────────────────────────────────

/** VOLT — assassin. Narrow, tall, forward-raked. Reads as fast standing still. */
function buildVolt(color) {
  const g = new THREE.Group();
  const body = createPlayerToonMaterial(color, { emissiveIntensity: 0.42 });
  // Blades in the character's own colour: white-transparent ones washed out
  // against the sky and read as ear tufts, not speed.
  const trim = createAccentMaterial(color, { transparent: true, opacity: 0.9 });

  g.add(mesh(geo('volt.core', () => new THREE.CapsuleGeometry(0.34, 1.05, 4, 10)), body, 0, 0.05, 0));
  const head = mesh(geo('volt.head', () => new THREE.ConeGeometry(0.32, 0.52, 6)), body, 0, 0.86, 0);
  head.rotation.x = -0.12;
  g.add(head);

  // Shoulder blades, swept back. The negative Z rake is the whole read — hard
  // enough that the tips clear the silhouette from the front.
  for (const side of [-1, 1]) {
    const blade = mesh(
      geo('volt.blade', () => new THREE.ConeGeometry(0.11, 0.85, 3)),
      trim,
      side * 0.38,
      0.3,
      -0.24
    );
    blade.rotation.set(1.15, 0, side * -0.55);
    g.add(blade);
  }

  addFace(g, { y: 0.48, z: 0.32, scale: 0.9 });
  addGlowRing(g, color);
  return g;
}

/** MONOLITH — juggernaut. Wide, blocky, low head. 150 HP has to be visible. */
function buildMonolith(color) {
  const g = new THREE.Group();
  const body = createPlayerToonMaterial(color, { emissiveIntensity: 0.22 });
  const plate = createPlayerToonMaterial(0x6b4a2f, { emissiveIntensity: 0.15 });

  g.add(mesh(geo('mono.torso', () => new THREE.BoxGeometry(1.12, 1.05, 0.82)), body, 0, 0.22, 0));
  g.add(mesh(geo('mono.hips', () => new THREE.BoxGeometry(0.9, 0.55, 0.72)), plate, 0, -0.62, 0));
  g.add(mesh(geo('mono.head', () => new THREE.BoxGeometry(0.46, 0.36, 0.44)), body, 0, 0.9, 0.02));

  for (const side of [-1, 1]) {
    g.add(
      mesh(
        geo('mono.pauldron', () => new THREE.BoxGeometry(0.42, 0.42, 0.7)),
        plate,
        side * 0.72,
        0.56,
        0
      )
    );
    g.add(
      mesh(geo('mono.fist', () => new THREE.BoxGeometry(0.36, 0.5, 0.36)), body, side * 0.72, -0.05, 0)
    );
  }

  addFace(g, { y: 0.92, z: 0.24, scale: 0.55, mouthDrop: 0.18 });
  addGlowRing(g, color);
  return g;
}

/** HEX — controller. Robed cone, no legs, orbiting rune ring. Never chases you. */
function buildHex(color) {
  const g = new THREE.Group();
  const body = createPlayerToonMaterial(color, { emissiveIntensity: 0.38 });
  const rune = createAccentMaterial(color, { transparent: true, opacity: 0.7 });

  g.add(mesh(geo('hex.robe', () => new THREE.ConeGeometry(0.62, 1.6, 10)), body, 0, -0.18, 0));
  g.add(mesh(geo('hex.head', () => new THREE.SphereGeometry(0.3, 12, 12)), body, 0, 0.82, 0));

  // The ring is animated by the pose driver; it is the character's tell.
  const ring = new THREE.Mesh(
    geo('hex.ring', () => new THREE.TorusGeometry(0.72, 0.045, 6, 24)),
    rune
  );
  ring.rotation.x = Math.PI / 2.3;
  ring.position.y = 0.3;
  ring.name = 'runeRing';
  ring.userData.noOutline = true;
  g.add(ring);

  addFace(g, { y: 0.88, z: 0.26, scale: 0.85 });
  addGlowRing(g, color);
  return g;
}

/** MIRAGE — trickster. Slim, split-tone, twin ribbons. Must read at a glance
 *  even as a decoy, because a decoy that looks different is not a decoy. */
function buildMirage(color) {
  const g = new THREE.Group();
  const body = createPlayerToonMaterial(color, { emissiveIntensity: 0.4 });
  // The split tone: hood in a darkened cut of the same colour, so the pale face
  // reads as a MASK — which is Mirage's whole identity — without breaking the
  // one-colour-per-character rule.
  const hoodColor = color.clone().multiplyScalar(0.45);
  const hood = createPlayerToonMaterial(hoodColor, { emissiveIntensity: 0.25 });
  const ribbon = createAccentMaterial(color, { transparent: true, opacity: 0.6 });

  g.add(mesh(geo('mir.core', () => new THREE.CapsuleGeometry(0.38, 0.95, 4, 10)), body, 0, 0.02, 0));
  const hoodMesh = mesh(geo('mir.hood', () => new THREE.SphereGeometry(0.38, 12, 10)), hood, 0, 0.84, -0.05);
  hoodMesh.scale.set(1, 0.95, 0.95);
  g.add(hoodMesh);
  const mask = mesh(geo('mir.mask', () => new THREE.SphereGeometry(0.33, 12, 10)), body, 0, 0.82, 0.05);
  mask.scale.set(0.92, 0.85, 0.8);
  g.add(mask);

  // Splayed outward so the tips clear the body from the FRONT — a decoy is
  // picked out at a glance from any angle or it is not doing its job.
  for (const side of [-1, 1]) {
    const tail = mesh(
      geo('mir.ribbon', () => new THREE.BoxGeometry(0.17, 1.1, 0.04)),
      ribbon,
      side * 0.36,
      0.3,
      -0.34
    );
    tail.rotation.set(0.55, 0, side * 0.5);
    tail.name = `ribbon${side > 0 ? 'R' : 'L'}`;
    tail.userData.noOutline = true;
    g.add(tail);
  }

  addFace(g, { y: 0.86, z: 0.33, scale: 0.8, mouthDrop: 0.16 });
  addGlowRing(g, color);
  return g;
}

/** TALON — artillery. Backpack cannon over one shoulder; the barrel is the tell. */
function buildTalon(color) {
  const g = new THREE.Group();
  const body = createPlayerToonMaterial(color, { emissiveIntensity: 0.3 });
  const metal = createPlayerToonMaterial(0x4b5566, { emissiveIntensity: 0.12 });

  g.add(mesh(geo('tal.core', () => new THREE.CapsuleGeometry(0.4, 0.9, 4, 10)), body, 0, 0, 0));
  const visor = mesh(geo('tal.head', () => new THREE.BoxGeometry(0.5, 0.34, 0.44)), body, 0, 0.84, 0);
  g.add(visor);
  g.add(
    mesh(
      geo('tal.visorGlass', () => new THREE.BoxGeometry(0.42, 0.12, 0.06)),
      createAccentMaterial(0x121a2e),
      0,
      0.86,
      0.23
    )
  );

  const pack = mesh(geo('tal.pack', () => new THREE.BoxGeometry(0.6, 0.6, 0.34)), metal, 0, 0.3, -0.4);
  g.add(pack);

  const barrel = mesh(
    geo('tal.barrel', () => new THREE.CylinderGeometry(0.11, 0.14, 1.15, 8)),
    metal,
    0.42,
    0.66,
    -0.16
  );
  barrel.rotation.set(-0.55, 0, -0.22);
  barrel.name = 'barrel';
  g.add(barrel);

  addFace(g, { y: 0.86, z: 0.26, scale: 0.55, mouthDrop: 0.28 });
  addGlowRing(g, color);
  return g;
}

const BUILDERS = {
  volt: buildVolt,
  monolith: buildMonolith,
  hex: buildHex,
  mirage: buildMirage,
  talon: buildTalon,
};

// ─── Procedural bodies (the shipping path) ─────────────────────
// The hand-built five above stay in the file as the rollback; nothing calls
// them any more. See docs/CHARACTERS.md — the body is now assembled from the
// parts kit by a seeded pure generator, so every player of a family is THEIR
// variant of it, identical on every screen.

/**
 * Naive procedural bodies build their hulls inside the pivots (so hulls follow
 * limbs and scattered pieces), which CheapOutline's registry never sees. This
 * set + listener keeps those hulls in step with the outline owner: hidden on
 * the pipeline tiers, shown on the hull tiers. Roots whose parent is gone are
 * dropped on the next toggle — disposal has many callers and none of them
 * report here.
 */
const _liveProcRoots = new Set();
let _outlineHooked = false;

function setProcHulls(root, visible) {
  for (const hull of root.userData.hullMeshes ?? []) hull.visible = visible;
}

function hookOutlineMode() {
  if (_outlineHooked) return;
  _outlineHooked = true;
  onOutlineModeChange((mode) => {
    for (const root of _liveProcRoots) {
      if (!root.parent) {
        _liveProcRoots.delete(root);
        continue;
      }
      setProcHulls(root, mode === 'hull');
    }
  });
}

/**
 * Builds a character.
 *
 * `characterId` is authoritative: it comes from the paid entry row, through
 * the room's `world_init` / snapshot, and never from anything the client
 * chose. `seedString` is the player's stable id — `world_init.you.userId`
 * locally, `pose.id` remotely — and everything below identity (head block,
 * limb styles, accessory, accent, face) is a pure function of it, identical
 * on every screen and stable across reconnects AND matches.
 *
 * `pooled: true` builds a proxy body for the CharacterBatcher (remote
 * players); the default builds real meshes (the local player, the mockup).
 *
 * Note `userData.colorHex` is the FAMILY colour from the blueprint, not
 * `CHARACTERS[id].colorHex` — nameplates, auras and dash ghosts tint from it,
 * so they follow the body. Telegraphs and ability VFX still read the protocol
 * table (see characterColor below); folding the two together is the roster-
 * colour pass CHARACTERS.md §7 flags for the protocol, deliberately not
 * smuggled in here.
 */
export function createCharacterModel(characterId, seedString = '', { pooled = false } = {}) {
  const def = CHARACTERS[characterId] ?? CHARACTERS.volt;
  const bp = blueprintFor(def.id, seedString || def.id);
  const group = assembleCharacter(bp, { proxy: pooled });

  // YXZ so pitch/roll compose INSIDE the yaw (assembleCharacter sets it too;
  // asserted here because the rotation order is a contract with the callers).
  group.rotation.order = 'YXZ';
  group.userData.characterId = def.id;
  group.userData.driver = createPoseDriver(group);
  group.castShadow = true;

  if (!pooled) {
    hookOutlineMode();
    setProcHulls(group, outlineMode() === 'hull');
    _liveProcRoots.add(group);
  }
  return group;
}

/**
 * The v1-era builder, kept callable for the rollback flag. Unused by the
 * game; deliberately not deleted for one release.
 */
export function createLegacyCharacterModel(characterId) {
  const def = CHARACTERS[characterId] ?? CHARACTERS.volt;
  const build = BUILDERS[def.id] ?? buildVolt;

  const group = build(new THREE.Color(def.colorHex));
  group.rotation.order = 'YXZ';
  group.userData.characterId = def.id;
  group.userData.colorHex = def.colorHex;
  group.castShadow = true;
  addHullOutline(group);
  return group;
}

/** The character's canonical colour. Deterministic, never per-instance random. */
export function characterColor(characterId) {
  const def = CHARACTERS[characterId] ?? CHARACTERS.volt;
  return new THREE.Color(def.colorHex);
}

export function characterColorHex(characterId) {
  const def = CHARACTERS[characterId] ?? CHARACTERS.volt;
  return def.colorHex;
}

// ─── Dash afterimages ───────────────────────────────────────────
// Spawned from position history rather than the dash effect flag: the flag
// arrives at snapshot latency for the local body, and the trail has to start
// on the dash's FIRST frame or it reads as lag, not speed.
const _ghosts = [];
let _ghostStamp = 0;
/** Above every legitimate sprint (26 x 1.25 fastest character ~= 32.5, measured
 *  33.5 with frame jitter), below DASH_SPEED 42. */
const TRAIL_SPEED = 37;
const GHOST_LIFE_MS = 260;

function spawnGhost(group, now) {
  const ghost = new THREE.Mesh(
    geo('ghostCapsule', () => new THREE.CapsuleGeometry(0.42, 0.95, 4, 10)),
    createAccentMaterial(group.userData.colorHex ?? 0xffffff, {
      transparent: true,
      opacity: 0.3,
      depthWrite: false,
    })
  );
  ghost.position.copy(group.position);
  ghost.rotation.copy(group.rotation);
  ghost.castShadow = false;
  ghost.userData.noOutline = true;
  ghost.userData.bornAt = now;
  group.parent.add(ghost);
  _ghosts.push(ghost);
}

function fadeGhosts(now) {
  // One pass per frame no matter how many characters call in.
  if (!_ghosts.length || now === _ghostStamp) return;
  _ghostStamp = now;
  for (let i = _ghosts.length - 1; i >= 0; i--) {
    const g = _ghosts[i];
    const age = now - g.userData.bornAt;
    if (age > GHOST_LIFE_MS) {
      g.parent?.remove(g);
      g.material.dispose();
      _ghosts.splice(i, 1);
    } else {
      const life = 1 - age / GHOST_LIFE_MS;
      g.material.opacity = 0.3 * life;
      g.scale.set(0.75 + 0.25 * life, 1, 0.75 + 0.25 * life);
    }
  }
}

/**
 * Dash afterimages for a procedural body. The pose driver owns every joint,
 * so this is the one piece of the old idle path the new one still wants:
 * speed from the group's own position history (identical for the predicted
 * local body and interpolated remotes), a ghost past TRAIL_SPEED.
 */
export function updateDashTrail(group, now) {
  const ud = group.userData;
  if (!ud.trailPrev) {
    ud.trailPrev = group.position.clone();
    ud.trailNow = now;
  }
  const dt = Math.min(0.1, Math.max(1e-4, (now - ud.trailNow) / 1000));
  let vx = (group.position.x - ud.trailPrev.x) / dt;
  let vz = (group.position.z - ud.trailPrev.z) / dt;
  ud.trailPrev.copy(group.position);
  ud.trailNow = now;
  // A spawn teleport or a reconciliation hard-snap is not motion.
  if (Math.hypot(vx, vz) > 80) vx = vz = 0;
  const speed = Math.hypot(vx, vz);

  if (speed > TRAIL_SPEED && group.parent && now - (ud.lastGhostAt ?? 0) > 30) {
    ud.lastGhostAt = now;
    spawnGhost(group, now);
  }
  fadeGhosts(now);
}

/**
 * Mirage's Fade, and the decoy shimmer.
 *
 * Note what this is NOT: it is not how invisibility is enforced. The server
 * omits an invisible opponent from the snapshot entirely, so an opponent's
 * client never receives a position to un-hide. This only dims YOUR OWN body
 * while you are faded.
 */
export function setCharacterShimmer(group, amount) {
  group.traverse((child) => {
    if (!child.isMesh || child.userData.isOutlineHull) return;
    const mat = child.material;
    if (!mat) return;
    if (amount <= 0) {
      if (child.userData.shimmerApplied) {
        mat.transparent = child.userData.origTransparent ?? false;
        mat.opacity = child.userData.origOpacity ?? 1;
        child.userData.shimmerApplied = false;
      }
      return;
    }
    if (!child.userData.shimmerApplied) {
      child.userData.origTransparent = mat.transparent;
      child.userData.origOpacity = mat.opacity;
      child.userData.shimmerApplied = true;
    }
    mat.transparent = true;
    mat.opacity = 1 - amount;
  });
  // A solid black hull around a 30%-opaque body defeats the fade, so the
  // outline goes away with it — and comes back, which is why this reads the
  // tier rather than its own previous value.
  const hull = group.userData.hullOutline;
  if (hull) hull.visible = amount <= 0 && outlineMode() === 'hull';
  // Procedural bodies carry their hulls per-part instead of in one group.
  if (group.userData.hullMeshes) {
    const show = amount <= 0 && outlineMode() === 'hull';
    for (const h of group.userData.hullMeshes) h.visible = show;
  }
}
